import { MUSIC_ACCESS_FLOW_VISUAL_RECIPE as recipe } from "./recipe";

/**
 * The fan sheet's own tokens, in the vocabulary of the ticketing engine's
 * drop-in checkout (`apps/ticketing-checkout`, `CheckoutAppearance`). Handed
 * to `LissenCheckout.mount` wherever the fan sheet embeds the checkout — the
 * Link in Bio event page and the editor's live preview — so the card fields,
 * the contact field and the cart read as part of the sheet rather than as a
 * partner panel inside it, for every organizer and with no branding to set.
 *
 * The page background stays transparent: the sheet already paints it. Stripe's
 * colour variables refuse translucent values, so `stripe.background` names
 * the opaque navy the sheet's translucent surfaces sit on for Stripe alone.
 */
export const EVENT_ACCESS_CHECKOUT_APPEARANCE = {
  colors: {
    background: "transparent",
    surface: recipe.colors.white10,
    text: recipe.colors.white,
    muted: recipe.colors.white70,
    accent: recipe.colors.white,
    onAccent: recipe.colors.black,
    danger: "#FFB4B4",
    border: recipe.colors.border,
  },
  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
  fontSize: 15,
  radius: recipe.geometry.cardRadius,
  buttons: {
    primary: {
      background: recipe.colors.white,
      text: recipe.colors.black,
      border: recipe.colors.white,
      radius: 999,
      fontWeight: 700,
      padding: "0 18px",
    },
    secondary: {
      background: "transparent",
      text: recipe.colors.white,
      border: recipe.colors.white20,
      radius: recipe.geometry.controlRadius,
      fontWeight: 600,
    },
  },
  input: {
    background: recipe.colors.white10,
    text: recipe.colors.white,
    placeholder: recipe.colors.white50,
    border: recipe.colors.white20,
    radius: recipe.geometry.controlRadius,
    focusBorder: recipe.colors.white70,
    focusShadow: "none",
  },
  stripe: { theme: "night", labels: "floating", background: "#253251" },
} as const;

/**
 * The light tickets sheet (Link in Bio, `.tickets-sheet-panel`): the same
 * drop-in, in the sheet's white. Mounted with `identity: "host"` — the sheet
 * has already verified the buyer — so these tokens only have to dress the
 * destination row, the express wallets, the card fields and the pay button.
 * The background is transparent: the sheet paints its own white.
 */
export const TICKETS_SHEET_CHECKOUT_APPEARANCE = {
  colors: {
    background: "transparent",
    surface: "#ffffff",
    text: "#1d1d1f",
    muted: "#6e6e73",
    accent: "#0082ec",
    onAccent: "#ffffff",
    danger: "#d70015",
    border: "#e5e5ea",
  },
  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
  fontSize: 15,
  radius: 12,
  buttons: {
    primary: {
      background: "#0082ec",
      text: "#ffffff",
      border: "#0082ec",
      radius: 12,
      fontWeight: 600,
    },
    secondary: {
      background: "#ffffff",
      text: "#1d1d1f",
      border: "#e5e5ea",
      radius: 12,
      fontWeight: 600,
    },
  },
  input: {
    background: "#ffffff",
    text: "#1d1d1f",
    placeholder: "#8e8e93",
    border: "#e5e5ea",
    radius: 12,
    focusBorder: "#0082ec",
    focusShadow: "0 0 0 3px rgba(0, 130, 236, 0.18)",
  },
  stripe: { theme: "stripe", labels: "above", background: "#ffffff" },
} as const;
